import { Loader2, Lock, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { toastError } from '@/components/ui/toast';
// the hook module, not the auth barrel: the barrel's components import onboarding.
import { useOAuthSession } from '@/features/auth/hooks/useOAuthSession';
import {
    AnchorChainToggle,
    anchorLabel,
    useAnchorChain,
    useEoaLogin,
    useWallet,
    useWalletNetwork,
} from '@/features/wallet';

interface WalletSetupStepProps {
    isSocialAuthed: boolean;
    isWalletAuthed: boolean;
    /**
     * Association run: a social session exists, but the account is created by an
     * owner EOA, so the social identity does NOT clear this step. Named after
     * what it requires rather than after the launch that set it, because the
     * requirement is the chain's, not the launcher's.
     */
    requiresOwnerWallet?: boolean;
    /** The social login being carried into the account, for the copy. */
    associateProviderName?: string | null;
    onContinue: () => void;
}

const POINTS: {
    icon: React.ComponentType<{ size?: number; className?: string }>;
    title: string;
    description: string;
}[] = [
    {
        icon: Sparkles,
        title: 'Trade without gas',
        description:
            'Snappy executes trades for you, you never sign or pay gas per trade.',
    },
    {
        icon: ShieldCheck,
        title: 'Bound to your wallet',
        description: 'Created from and linked to the wallet you connect.',
    },
    {
        icon: Lock,
        title: 'You stay in control',
        description: 'Only your connected wallet can withdraw the funds.',
    },
];

/**
 * Step 3 — Snappy Wallet (gasless custodial wallet) setup. One way in: the
 * connected wallet. The account is created by owner signatures, so a social
 * login cannot produce one; social sign-in is offered as an optional extra on
 * the funding step, once the account it would attach to exists.
 *
 * An identity that already clears the step is not asked to press anything: the
 * step advances itself. Back-navigation (the progress bar) therefore bounces
 * straight forward again, which is the honest answer, the step has nothing left
 * to do for that user. A wallet that is merely connected is NOT signed in for
 * automatically: the signature is a deliberate act, so it stays behind the
 * button.
 */
const WalletSetupStep: React.FC<WalletSetupStepProps> = ({
    isSocialAuthed,
    isWalletAuthed,
    requiresOwnerWallet = false,
    associateProviderName = null,
    onContinue,
}) => {
    const { address, isConnected, connect } = useWallet();
    const { ensureAnchor } = useWalletNetwork();
    const { anchorChainId, selectAnchorChain } = useAnchorChain();
    const { me } = useOAuthSession();
    const { login } = useEoaLogin();
    const [isWorking, setIsWorking] = useState(false);

    const continueWithWallet = useCallback(async () => {
        setIsWorking(true);
        try {
            if (!isConnected || !address) {
                await connect();
                return;
            }
            if (!(await ensureAnchor())) {
                throw new Error(
                    `Switch your connected wallet to ${anchorLabel(anchorChainId)}.`,
                );
            }
            await login(address);
            onContinue();
        } catch (caught) {
            toastError(caught, { operation: 'Sign-in' });
        } finally {
            setIsWorking(false);
        }
    }, [
        address,
        anchorChainId,
        connect,
        ensureAnchor,
        isConnected,
        login,
        onContinue,
    ]);

    const isAuthed = isWalletAuthed || (isSocialAuthed && !requiresOwnerWallet);

    // Auto-skip. The ref is what keeps it to one call: `onContinue` changes
    // identity upstream, and a second call would skip a step the user never
    // saw. It survives the component's own re-renders and StrictMode's double
    // mount; a genuine return to this step remounts it, and firing again there
    // is the point.
    const autoContinuedRef = useRef(false);
    useEffect(() => {
        if (!isAuthed || autoContinuedRef.current) return;
        autoContinuedRef.current = true;
        onContinue();
    }, [isAuthed, onContinue]);

    return (
        <div className="flex flex-col items-center px-6 pb-6 pt-2 text-center">
            <div className="flex size-16 items-center justify-center rounded-2xl border border-border bg-secondary/50">
                <Wallet size={30} className="text-foreground/80" />
            </div>

            <h2 className="mt-4 text-xl font-bold tracking-tight text-foreground">
                Set up your Snappy Wallet
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
                A gasless trading wallet that does the signing for you.
            </p>

            {isAuthed ? (
                <div className="mt-6 flex w-full items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Loader2 size={16} className="animate-spin" />
                    Continuing to the next step
                </div>
            ) : (
                <div className="mt-6 flex w-full flex-col gap-3">
                    <AnchorChainToggle
                        value={anchorChainId}
                        onChange={selectAnchorChain}
                        anchors={me?.anchors ?? null}
                        disabled={isWorking}
                        description={`Your account will live on ${anchorLabel(anchorChainId)}.`}
                    />
                    <Button
                        size="lg"
                        className="justify-center gap-2 font-semibold"
                        disabled={isWorking}
                        onClick={() => void continueWithWallet()}
                    >
                        {isWorking && (
                            <Loader2 size={16} className="animate-spin" />
                        )}
                        <Wallet size={16} />
                        Continue with Wallet
                    </Button>
                    <p className="text-xs text-muted-foreground">
                        {requiresOwnerWallet
                            ? `Your ${associateProviderName ?? 'social'} login needs an account to belong to. Connect the wallet that will own it, and the login is added to it during setup.`
                            : 'A new account is created by your wallet. You can add social sign-in methods once it exists.'}
                    </p>
                </div>
            )}

            <div className="mt-6 w-full divide-y divide-border rounded-xl border border-border text-left">
                {POINTS.map(({ icon: Icon, title, description }) => (
                    <div key={title} className="flex items-center gap-3 p-3.5">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-secondary/40">
                            <Icon size={18} className="text-foreground/80" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">
                                {title}
                            </p>
                            <p className="text-xs leading-relaxed text-muted-foreground">
                                {description}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default WalletSetupStep;
